// Copyright © 2019-2026 Focal Systems. All rights reserved.

export type PageContextKind = "chain" | "store" | "setting" | "section";

/** What the viewer is looking at. `path` is the pathname and doubles as identity. */
export interface PageContext {
  kind: PageContextKind;
  label: string;
  path: string;
}

export interface Step {
  id: string;
  label: string;
  status: "running" | "done";
}

export interface AnswerTable {
  columns: string[];
  rows: string[][];
}

/** `href` is an in-app path (optionally with a query), navigated client-side. */
export interface AnswerLink {
  label: string;
  href: string;
}

export type AssistantEvent =
  | { type: "step"; id: string; label: string; status: "running" | "done" }
  | { type: "text"; delta: string }
  | { type: "table"; table: AnswerTable }
  | { type: "link"; link: AnswerLink }
  | { type: "note"; text: string }
  | { type: "error"; message: string }
  | { type: "done" };

export interface AssistantTurn {
  question: string;
  answer: string;
}

/** What the service accepts (AskRequest in focal-systems/bot-backoffice, src/assistant/models.py); more is refused with 422. */
export const MAX_QUESTION_CHARS = 2000;
export const MAX_ANSWER_CHARS = 8000;
export const MAX_HISTORY_TURNS = 10;

export interface AssistantRequest {
  question: string;
  context: PageContext | null;
  history: AssistantTurn[];
}

/** The seam a real backend (SSE) will implement. Must stop yielding once `signal` aborts. */
export interface AssistantTransport {
  ask(request: AssistantRequest, signal: AbortSignal): AsyncIterable<AssistantEvent>;
}

export type AnswerStatus = "working" | "streaming" | "done" | "stopped" | "error";

export interface UserMessage {
  id: string;
  role: "user";
  text: string;
}

export interface AssistantMessage {
  id: string;
  role: "assistant";
  question: string;
  context: PageContext | null;
  status: AnswerStatus;
  steps: Step[];
  text: string;
  tables: AnswerTable[];
  links: AnswerLink[];
  notes: string[];
  error: string | null;
  feedback: "up" | "down" | null;
}

export interface DividerMessage {
  id: string;
  role: "divider";
  label: string;
}

export type Message = UserMessage | AssistantMessage | DividerMessage;
